import { useEffect, useRef } from 'react'
import { isActivityFree } from '../../../../pure-js/libs/ActivityHelper'
import { Activity, Client, Enrollment, EnrollmentPaymentStatus } from '../../../../pure-js/types/GrooverTypes'
import {
  gtmEventId,
  hasFiredGtmEvent,
  pushBookingEvent,
  pushBookingPaidEvent,
  shouldPushBookingPaid
} from './gtmEvents'
import { GtmRegistrationEvent } from './gtmTypes'

export type UseGtmRegistrationEventsArgs = {
  gtmId: string | undefined
  client: Client | undefined
  urlEnrollmentId: string | undefined
  stateEnrollmentId: string | undefined
  isPaid: boolean
  paymentStatus: EnrollmentPaymentStatus | undefined
  primaryActivity: Activity | undefined
  selectedActivities: Activity[]
  obfuscatedEnrollment?: Pick<Enrollment, 'id' | 'priceDetails' | 'activities' | 'paymentStatus'> | null
  activeRegisterStep: number
  fallbackValueMajor: number
}

export const useGtmRegistrationEvents = ({
  gtmId,
  client,
  urlEnrollmentId,
  stateEnrollmentId,
  isPaid,
  paymentStatus,
  primaryActivity,
  selectedActivities,
  obfuscatedEnrollment,
  activeRegisterStep,
  fallbackValueMajor
}: UseGtmRegistrationEventsArgs) => {
  const arrivalPaidByEnrollmentRef = useRef<Record<string, boolean>>({})
  const lastTrackedEnrollmentIdRef = useRef<string | undefined>(undefined)

  const enrollmentId = stateEnrollmentId || undefined
  const additionalActivities =
    obfuscatedEnrollment?.activities && obfuscatedEnrollment.activities.length > 0
      ? obfuscatedEnrollment.activities
      : selectedActivities

  useEffect(() => {
    if (!enrollmentId) {
      lastTrackedEnrollmentIdRef.current = undefined
      return
    }

    if (lastTrackedEnrollmentIdRef.current !== enrollmentId) {
      lastTrackedEnrollmentIdRef.current = enrollmentId
      delete arrivalPaidByEnrollmentRef.current[enrollmentId]
    }

    if (arrivalPaidByEnrollmentRef.current[enrollmentId] !== undefined) return

    if (!urlEnrollmentId) {
      arrivalPaidByEnrollmentRef.current[enrollmentId] = false
      return
    }

    if (obfuscatedEnrollment?.id !== enrollmentId) return

    arrivalPaidByEnrollmentRef.current[enrollmentId] =
      obfuscatedEnrollment.paymentStatus === EnrollmentPaymentStatus.Paid || isPaid
  }, [enrollmentId, urlEnrollmentId, obfuscatedEnrollment?.id, obfuscatedEnrollment?.paymentStatus, isPaid])

  useEffect(() => {
    if (!gtmId || !client || !primaryActivity || !enrollmentId) return
    if (urlEnrollmentId) return

    pushBookingEvent({
      gtmId,
      enrollmentId,
      client,
      primaryActivity,
      additionalActivities,
      enrollment: obfuscatedEnrollment,
      fallbackValueMajor
    })
  }, [
    gtmId,
    client,
    primaryActivity,
    enrollmentId,
    urlEnrollmentId,
    additionalActivities,
    obfuscatedEnrollment,
    fallbackValueMajor
  ])

  useEffect(() => {
    if (!gtmId || !client || !primaryActivity || !enrollmentId) return
    if (arrivalPaidByEnrollmentRef.current[enrollmentId] === undefined) return

    const isFreeCourseOnStep5 = isActivityFree(primaryActivity, additionalActivities) && activeRegisterStep === 5

    if (
      !shouldPushBookingPaid({
        isPaid,
        paymentStatus,
        isFreeCourseOnStep5,
        arrivedAlreadyPaid: arrivalPaidByEnrollmentRef.current[enrollmentId] === true,
        bookingFiredInSession: hasFiredGtmEvent(gtmEventId(enrollmentId, GtmRegistrationEvent.Booking)),
        isUrlResume: Boolean(urlEnrollmentId)
      })
    ) {
      return
    }

    pushBookingPaidEvent({
      gtmId,
      enrollmentId,
      client,
      primaryActivity,
      additionalActivities,
      enrollment: obfuscatedEnrollment,
      fallbackValueMajor
    })
  }, [
    gtmId,
    client,
    primaryActivity,
    enrollmentId,
    urlEnrollmentId,
    isPaid,
    paymentStatus,
    additionalActivities,
    obfuscatedEnrollment,
    activeRegisterStep,
    fallbackValueMajor
  ])
}
